import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { CheckCircle, ArrowRight, Sparkles, AlertTriangle } from 'lucide-react'
import { useSessionStore } from '@/stores'
import { cn } from '@utils'

interface SessionCompleteProps {
  children: React.ReactNode
  /** Optional override for the action button. When omitted, defaults to logout + restart. */
  actionLabel?: string
  onAction?: () => void
  /** Override the headline above the message. */
  heading?: string
  /** 'error' swaps the tick for a warning glyph and the accent for red. */
  tone?: 'success' | 'error'
}

const SessionComplete = ({
  children,
  actionLabel,
  onAction,
  heading = 'Thank You!',
  tone = 'success',
}: SessionCompleteProps) => {
  const isError = tone === 'error'
  const navigate = useNavigate()
  const sessionStore = useSessionStore()

  const handleAction = () => {
    if (onAction) {
      return onAction()
    }
    sessionStore.logout()
    navigate('/session', { replace: true })
  }

  return (
    <div className="flex justify-center px-4 pt-12 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] as const }}
        className={cn(
          'session-complete-card relative max-w-xl w-full mx-auto px-8 sm:px-12 py-12 sm:py-14 text-center',
          isError && 'session-complete-error'
        )}>
        <motion.div
          initial={{ scale: 0, rotate: -45, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.15 }}
          className="relative w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-7"
          style={{
            background:
              'conic-gradient(from 180deg, color-mix(in srgb, var(--session-complete-accent) 20%, var(--content-background-color)), color-mix(in srgb, var(--session-complete-accent) 8%, var(--content-background-color)), color-mix(in srgb, var(--session-complete-accent) 20%, var(--content-background-color)))',
          }}>
          <div
            className="absolute inset-1 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--content-background-color, #fff)' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 0.35 }}>
              {isError ? (
                <AlertTriangle size={42} strokeWidth={1.75} style={{ color: 'var(--session-complete-accent)' }} />
              ) : (
                <CheckCircle size={44} strokeWidth={1.75} style={{ color: 'var(--session-complete-accent)' }} />
              )}
            </motion.div>
          </div>
          {!isError && (
            <motion.span
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              className="absolute -top-1 -right-1"
              style={{ color: 'var(--session-complete-accent)' }}>
              <Sparkles size={20} strokeWidth={2} />
            </motion.span>
          )}
        </motion.div>

        <div className="step-intro">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="text-fluid-3xl font-bold mb-3 tracking-tight"
            style={{ color: 'var(--text-color)' }}>
            {heading}
          </motion.h1>
          <hr />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="text-fluid-lg leading-relaxed mb-9"
          style={{ color: 'var(--light-text-color)' }}>
          {children}
        </motion.div>

        <motion.button
          type="button"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleAction}
          className="session-complete-action group inline-flex items-center justify-center gap-2 py-3.5 px-10 w-full max-w-xs mx-auto transition-shadow no-transform">
          {actionLabel || 'Start New Session'}
          <motion.span
            className="inline-flex"
            initial={false}
            animate={{ x: 0 }}
            whileHover={{ x: 3 }}
            transition={{ type: 'spring', stiffness: 400, damping: 18 }}>
            <ArrowRight size={18} />
          </motion.span>
        </motion.button>
      </motion.div>
    </div>
  )
}

export default SessionComplete
